/**
 * 关于页面模块
 * 显示应用信息、版本、功能特色、更新公告等
 */
import './about.css';

// 更新公告数据
interface UpdateLog {
  version: string;
  date: string;
  type: 'feature' | 'fix' | 'improve';
  title: string;
  changes: string[];
}

const UPDATE_LOGS: UpdateLog[] = [
  {
    version: 'v1.0.0',
    date: '2025-12-11',
    type: 'feature',
    title: '正式版即将发布',
    changes: [
      '正式版本上线，率先支持搭载HarmonyOS系统的手机、平板、PC',
      '新增关于页面与更新公告功能',
      '优化整体性能与稳定性',
      '支持HarmonyOS API20',
      '新版指南模块上线',
      '新增设置-渲染器帧率监控开关',
      '新增设置-积木器默认执行速度开关',
      '修复小屏设备的异常显示问题',
      '修复低概率初始资源错误问题',
      '修复小屏设备舞台视角控制器消失问题',
      '修复HarmonyOS API17部分设备的异常显示问题',
      '修复已知的若干问题',
    ]
  },
  {
    version: 'v0.7.0 RC',
    date: '2025-11-23',
    type: 'feature',
    title: '积木编程模块上线',
    changes: [
      '新增积木编程模块，支持拖拽式代码编写',
      '支持多种积木分类：事件、控制、运算、变量等',
      '新增预设案例库，快速上手',
      '修复已知的若干问题',

    ]
  },
  {
    version: 'v0.6.1',
    date: '2025-11-06',
    type: 'feature',
    title: '设置系统全面升级',
    changes: [
      '新增设置模块，支持通用、编辑器、渲染器配置',
      '新增动画风格选择：默认/清爽',
      '新增编辑器主题设置',
      '修复已知的若干问题'
    ]
  },
  {
    version: 'v0.6.0',
    date: '2025-11-01',
    type: 'feature',
    title: '答题统计与分析',
    changes: [
      '新增答题统计功能，查看学习数据',
      '支持按算法类型统计正确率',
      '新增我的记录功能，查看历史答题',
      '优化答题结果展示效果',
      '修复已知的若干问题'
    ]
  },
  {
    version: 'v0.3.0',
    date: '2025-10-19',
    type: 'feature',
    title: '编排刷题模式',
    changes: [
      '新增编排刷题功能，自定义题目顺序',
      '支持多选题目批量练习',
      '新增筛选功能：按类型、难度、状态',
      '优化题目列表加载速度',
      '修复已知的若干问题',
    ]
  },
  {
    version: 'v0.2.3 RC',
    date: '2025-10-03',
    type: 'feature',
    title: '代码编辑器增强',
    changes: [
      '新增在线代码编辑器，支持语法高亮',
      '支持 JavaScript、Python、Java、C++ 多语言',
      '新增代码运行功能，实时查看结果',
      '支持查看参考答案',
      '修复已知的若干问题'
    ]
  },
  {
    version: 'v0.2.3',
    date: '2025-10-01',
    type: 'feature',
    title: '算法面板增强',
    changes: [
      '新增算法代码面板，查看源码实现',
      '支持 JS、Python、C++ 三种语言切换',
      '新增算法介绍面板，包含时间复杂度分析',
      '优化下半区布局体验',
      '修复已知的若干问题'
    ]
  },
    {
    version: '其它',
    date: '2025-09-20',
    type: 'improve',
    title: '参加内测，体验新功能',
    changes: [
      '哈喽，大家好！CodePlanet现已开启内测报名，您将第一时间体验到最新功能。同时也有部分注意事项需要您了解：',
      '内测版本并不代表最终品质，仅供测试使用。且可能存在诸多问题。',
      '我们将会根据用户反馈不断优化和改进，力求为用户提供更好的体验。',
      '如需报名内测，请将华为账号所绑定的手机号发送至邮箱：1599055925@qq.com，审核通过后，我们将会第一时间为您发布邀请测试，同时我们也将对您的个人信息进行保密。'
    ]
  },
  {
    version: 'v0.1.1',
    date: '2025-09-20',
    type: 'feature',
    title: '播放控制系统',
    changes: [
      '新增完整的播放控制系统',
      '支持播放、暂停、单步、重置操作',
      '新增速度调节功能：0.5x ~ 2x',
      '新增变量观察窗，实时查看数据变化',
      '修复已知的若干问题'
    ]
  },
  {
    version: 'v0.1.0',
    date: '2025-09-13',
    type: 'feature',
    title: '你好，世界',
    changes: [
      'CodePlanet首次上线'
    ]
  }
];

class AboutModule {
  private container: HTMLElement | null = null;
  private initialized = false;
  private currentTab = 'info'; // 'info' | 'updates'

  /**
   * 初始化关于模块
   */
  init(): void {
    if (this.initialized) return;
    
    this.container = document.getElementById('page-about');
    if (!this.container) {
      console.warn('关于页面容器未找到');
      return;
    }

    this.render();
    this.bindEvents();
    this.initTabIndicator();
    this.initialized = true;
    console.log('✅ 关于模块初始化完成');
  }

  /**
   * 渲染关于页面内容
   */
  private render(): void {
    if (!this.container) return;

    this.container.innerHTML = `
      <!-- 二级TAB导航 -->
      <div class="about-tabs">
        <button class="about-tab active" data-tab="info">关于</button>
        <button class="about-tab" data-tab="updates">更新公告</button>
      </div>
      
      <!-- 关于视图 -->
      <div id="about-view-info" class="about-view active">
        ${this.renderInfoView()}
      </div>
      
      <!-- 更新公告视图 -->
      <div id="about-view-updates" class="about-view">
        ${this.renderUpdatesView()}
      </div>
    `;
  }

  /**
   * 渲染关于信息视图
   */
  private renderInfoView(): string {
    return `
      <div class="about-page">
        <div class="about-content">
          <div class="about-header">
            <div class="about-logo">
              <img src="/logo.png" alt="Logo" class="logo-icon" width="64" height="64" />
            </div>
            <h1 class="about-title">Code Planet</h1>
            <p class="about-version">v1.0.0</p>
          </div>
          
          <div class="about-description">
            <p>在光与影的交织中，让代码有形，让算法可见。</p>
          </div>
          
          <div class="about-footer">
            <p class="about-copyright">© 2025 Code Planet. All rights reserved.</p>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * 渲染更新公告视图
   */
  private renderUpdatesView(): string {
    const logsHtml = UPDATE_LOGS.map(log => {
      const typeClass = `update-type-${log.type}`;
      
      return `
        <div class="update-card">
          <div class="update-card-header">
            <div class="update-version-badge ${typeClass}">
              <span>${log.version}</span>
            </div>
            <span class="update-date">${log.date}</span>
          </div>
          <h3 class="update-card-title">${log.title}</h3>
          <ul class="update-changes">
            ${log.changes.map(change => `<li>${change}</li>`).join('')}
          </ul>
        </div>
      `;
    }).join('');

    return `
      <div class="updates-page">
        <div class="updates-content">
          <div class="updates-header">
            <h2>更新公告</h2>
            <p>了解最新功能与改进</p>
          </div>
          <div class="updates-list">
            ${logsHtml}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * 绑定事件
   */
  private bindEvents(): void {
    if (!this.container) return;

    // TAB 切换
    const tabs = this.container.querySelectorAll('.about-tab');
    tabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const tabId = target.dataset.tab;
        if (tabId) {
          this.switchTab(tabId);
        }
      });
    });
  }

  /**
   * 切换TAB
   */
  private switchTab(tabId: string): void {
    if (!this.container) return;
    this.currentTab = tabId;

    // 更新TAB按钮状态
    const tabs = this.container.querySelectorAll('.about-tab');
    tabs.forEach(tab => {
      tab.classList.toggle('active', tab.getAttribute('data-tab') === tabId);
    });

    // 更新指示器位置
    const activeTab = this.container.querySelector('.about-tab.active') as HTMLElement;
    if (activeTab) {
      this.updateTabIndicator(activeTab);
    }

    // 更新视图显示
    const views = this.container.querySelectorAll('.about-view');
    views.forEach(view => {
      const viewEl = view as HTMLElement;
      viewEl.classList.toggle('active', viewEl.id === `about-view-${tabId}`);
    });
  }

  /**
   * 初始化TAB滑动指示器
   */
  private initTabIndicator(): void {
    if (!this.container) return;
    const tabsContainer = this.container.querySelector('.about-tabs');
    if (!tabsContainer) return;

    // 创建指示器元素
    let indicator = tabsContainer.querySelector('.about-tab-indicator') as HTMLElement;
    if (!indicator) {
      indicator = document.createElement('div');
      indicator.className = 'about-tab-indicator';
      tabsContainer.insertBefore(indicator, tabsContainer.firstChild);
    }

    // 初始化位置
    const initPosition = () => {
      const activeTab = tabsContainer.querySelector('.about-tab.active') as HTMLElement;
      if (activeTab && activeTab.getBoundingClientRect().width > 0) {
        this.updateTabIndicator(activeTab);
      }
    };

    initPosition();
    setTimeout(initPosition, 100);
    setTimeout(initPosition, 300);
  }

  /**
   * 更新TAB指示器位置
   */
  private updateTabIndicator(activeTab: HTMLElement): void {
    if (!this.container) return;
    const tabsContainer = this.container.querySelector('.about-tabs') as HTMLElement;
    const indicator = tabsContainer?.querySelector('.about-tab-indicator') as HTMLElement;
    if (!tabsContainer || !indicator) return;

    const containerRect = tabsContainer.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();

    const left = tabRect.left - containerRect.left;
    const width = tabRect.width;

    indicator.style.left = `${left}px`;
    indicator.style.width = `${width}px`;
  }

  /**
   * 刷新页面（切换到关于页时调用）
   */
  refresh(): void {
    // 重新初始化指示器位置
    const activeTab = this.container?.querySelector('.about-tab.active') as HTMLElement;
    if (activeTab) {
      this.updateTabIndicator(activeTab);
    }
  }
}

// 导出单例
export const aboutModule = new AboutModule();
